import { Address } from "../components/form-components/AddressForm";
import { Authorization } from "../components/form-components/AuthorizationForm";
import { BishopsReligious } from "../components/form-components/BishopsReligiousForm";
import { ConvictedOfCrime } from "../components/form-components/ConvictedOfCrimeForm";
import { EducationalInstitution } from "../components/form-components/EducationalInstitutionForm";
import { Education } from "../components/form-components/EducationForm";
import { EmployerCongregation } from "../components/form-components/EmployerCongregationForm";
import { LegalName } from "../components/form-components/LegalNamesForm";
import { PersonalInformation } from "../components/form-components/PersonalInformationForm";
import { AdditionalForm } from "../interfaces/additionalForm";
import { CandidateQuestionnaire } from "../interfaces/candidateQuestionnaire";
import { Client } from "../interfaces/client";
import { CriminalOrder } from "../interfaces/criminalOrder";
import { Degree } from "../interfaces/degree";
import { Employer } from "../interfaces/employer";
import { Order } from "../interfaces/order";
import { OrderForm } from "../interfaces/orderForm";
import { OrderFormType } from "../interfaces/orderFormType";
import { QuestionOption } from "../interfaces/questionOption";
import { ReferenceOrder } from "../interfaces/referenceOrder";
import { SocialProfile } from "../interfaces/socialProfile";
import { SubscriptionOrder } from "../interfaces/subscriptionOrder";
import { CreditRelease } from "../pages/candidate-pages/candidate-forms/CreditReportAuthorizationForm";
import { MotorRelease } from "../pages/candidate-pages/candidate-forms/MotorVehicleAuthorizationForm";
import { ReferenceIdentification } from "../pages/candidate-pages/candidate-forms/ReferenceIdentificationForm";
import { SocialMediaRelease } from "../pages/candidate-pages/candidate-forms/SocialMediaAuthorizationForm";
import { TransferRelease } from "../pages/candidate-pages/candidate-forms/TransferReleaseForm";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBlobToBase64, convertFileToBase64 } from "./helpers";
import { HttpService } from "./httpService";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getOrderFormsByOrderID = async (
  orderID: number,
): Promise<OrderForm[] | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderForm/GetOrderFormsByOrderID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch order forms: ${response.statusText}`);
  }

  if (response.status === 204) {
    return null;
  }

  const orderForms: OrderForm[] = await response.json();
  return orderForms;
};

export const getOrderFormByID = async (
  orderFormID: number,
): Promise<OrderForm | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderForm/GetOrderFormByID/${orderFormID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch order form: ${response.statusText}`);
  }

  if (response.status === 204) {
    return null;
  }

  const orderForm: OrderForm = await response.json();
  return orderForm;
};

export const getOrderFormByOrderIDType = async (
  orderID: number,
  typeID: number,
): Promise<OrderForm | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const response = await fetch(
    `${apiBaseUrl}/api/OrderForm/GetOrderFormByOrderIDType/${orderID}/${typeID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch order form: ${response.statusText}`);
  }

  if (response.status === 204) {
    return null;
  }

  const orderForm: OrderForm = await response.json();
  return orderForm;
};

export const getOrderFormTypes = async (): Promise<OrderFormType[] | null> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderForm/GetOrderFormTypes`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch order form types: ${response.statusText}`);
  }

  if (response.status === 204) {
    return null;
  }

  const orderFormTypes: OrderFormType[] = await response.json();
  return orderFormTypes;
};

export const createOrderForm = async (
  orderFormTypeID: number,
  orderID: number,
): Promise<OrderForm> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderForm/CreateOrderForm/${orderID}/${orderFormTypeID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Order Form Error:", errorText);
    throw new Error("Failed to create the order form.");
  }

  const createdOrderForm: OrderForm = await response.json();
  return createdOrderForm;
};

export const deleteOrderForm = async (orderFormID: number): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/OrderForm/DeleteOrderForm/${orderFormID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Order Form ID ${orderFormID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Delete Order Form Error:", errorText);
    throw new Error(`Failed to delete order form with ID ${orderFormID}.`);
  }
};

export const uploadCandidateOrderForm = async (
  order: Order,
  selectedOrderFormType: OrderFormType,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      order,
      selectedOrderFormType,
      fileBase64: base64Data,
      contentType,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/UploadCandidateOrderForm`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload candidate order Form.");
      return false;
    }

    console.log("Form successfully uploaded.");
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const uploadCandidateForm = async (
  order: Order,
  selectedOrderFormType: OrderFormType,
  file: File,
): Promise<boolean> => {
  try {
    const { base64Data, contentType } = await convertFileToBase64(file);

    const formData = {
      order,
      selectedOrderFormType,
      fileBase64: base64Data,
      contentType,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/UploadCandidateForm`,

      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to upload transfer order Form.");
      return false;
    }

    console.log("Form successfully uploaded.");
    return true;
  } catch (error) {
    console.error("Error while uploading form:", error);
    return false;
  }
};

export const saveCriminalRecordsAuthForm = async (
  criminalOrderID: number,
  orderFormID: number,
  personalInformation: PersonalInformation,
  convictedOfCrimes: ConvictedOfCrime[],
  legalNames: LegalName[],
  residentialAddresses: Address[],
  employmentAddresses: Address[],
  authorization: Authorization,
  criminalYears: number,
  diocese: string,
  abbreviation: string,
  hasEmploymentService: boolean,
  hasEducation: boolean,
  file?: File,
  educations?: Education[],
  oneEducation?: boolean,
  hasMonitoringService?: boolean,
): Promise<boolean> => {
  try {
    let base64Data = "";

    if (file) {
      ({ base64Data } = await convertFileToBase64(file));
    }

    const formData = {
      criminalOrderID,
      orderFormID,
      personalInformation,
      convictedOfCrimes,
      legalNames,
      residentialAddresses,
      employmentAddresses,
      authorization,
      criminalYears,
      diocese,
      abbreviation,
      hasEmploymentService,
      hasEducation,
      base64File: base64Data,
      educations,
      oneEducation,
      hasMonitoringService,
    };

    const response = await fetch(
      `${apiBaseUrl}/api/OrderForm/SaveCriminalRecordsAuthForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Criminal Records Authorization Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveBackgroundCheckAgreementForm = async (
  order: Order,
  orderFormID: number,
  diocese: string,
  authorization: Authorization,
): Promise<boolean> => {
  const formData = {
    order,
    orderFormID,
    diocese,
    authorization,
  };
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveBackgroundCheckAgreementForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Background Check Agreement Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveCreditReportAuthorizationForm = async (
  order: Order,
  orderFormID: number,
  creditRelease: CreditRelease,
  authorization: Authorization,
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  abbreviation: string,
  clientName: string,
): Promise<boolean> => {
  try {
    const formData = {
      order,
      orderFormID,
      creditRelease,
      authorization,
      clientFirstName,
      clientLastName,
      clientTitle,
      diocese,
      abbreviation,
      clientName,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveCreditReportAuthorizationForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Credit Report Authorization Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveMotorVehicleAuthorizationForm = async (
  order: Order,
  orderFormID: number,
  motorRelease: MotorRelease,
  authorization: Authorization,
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  clientName: string,
  file?: File,
): Promise<boolean> => {
  try {
    let base64Data = "";

    if (file) {
      ({ base64Data } = await convertFileToBase64(file));
    }

    const formData = {
      order,
      orderFormID,
      motorRelease,
      authorization,
      clientFirstName,
      clientLastName,
      clientTitle,
      diocese,
      base64File: base64Data,
      clientName,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveMotorVehicleAuthorizationForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to save the form.");
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveReferenceScreeningAuthForm = async (
  order: Order,
  orderFormID: number,
  authorization: Authorization,
  clientDiocese: string,
  clientDiocese2: string,
  clientName: string,
  referenceOrder: ReferenceOrder,
  logoForm: AdditionalForm | null,
  clientID: number,
): Promise<boolean> => {
  try {
    let signedLogoBase64 = "";
    if (logoForm) {
      const signedLogoUrl = await HttpService.getFile(
        "page-decorations",
        order.clientID.toString(),
        logoForm?.completedFormUrl,
      );

      const response = await fetch(signedLogoUrl);
      const blob = await response.blob();
      signedLogoBase64 = await convertBlobToBase64(blob);
    }

    const formData = {
      order,
      orderFormID,
      authorization,
      clientDiocese,
      clientDiocese2,
      clientName,
      referenceOrder,
      signedLogoBase64,
      clientID,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveReferenceScreeningAuthForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Reference Screening Auth Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveReferenceIdentificationForm = async (
  order: Order,
  orderFormID: number,
  referenceIdentification: ReferenceIdentification,
  otherNames: LegalName[],
  educationalInstitutions: EducationalInstitution[],
  employerCongregations: EmployerCongregation[],
  bishopsReligious: BishopsReligious[],
  authorization: Authorization,
  referenceOrder: ReferenceOrder,
  diocese: string,
  clientID: number,
): Promise<boolean> => {
  try {
    const formData = {
      order,
      orderFormID,
      referenceIdentification,
      otherNames,
      educationalInstitutions,
      employerCongregations,
      bishopsReligious,
      authorization,
      referenceOrder,
      diocese,
      clientID,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveReferenceIdentificationForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Reference Identification Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveCandidateScreeningQuestionnaire = async (
  order: Order,
  orderFormID: number,
  responses: { [key: string]: string },
  questionsWithOptions: Map<
    number,
    { questionText: string; options: QuestionOption[] }
  >,
  authorization: Authorization,
  comments: string,
  candidateQuestionnaire: CandidateQuestionnaire,
  diocese: string,
  diocese2: string,
  logoForm: AdditionalForm | null,
): Promise<boolean> => {
  let signedLogoBase64 = "";
  if (logoForm) {
    const signedLogoUrl = await HttpService.getFile(
      "page-decorations",
      order.clientID.toString(),
      logoForm?.completedFormUrl,
    );

    const response = await fetch(signedLogoUrl);
    const blob = await response.blob();
    signedLogoBase64 = await convertBlobToBase64(blob);
  }

  const responsesList = Object.entries(responses).map(([key, value]) => ({
    responses: { [key]: value },
  }));

  const questionsList = Array.from(questionsWithOptions.entries()).map(
    ([key, value]) => ({
      questionID: key,
      questionText: value.questionText,
      options: value.options.map((option) => ({
        questionOptionID: option.questionOptionID,
        questionOptionText: option.questionOptionText,
      })),
    }),
  );

  try {
    const formData = {
      order,
      orderFormID,
      responses: responsesList,
      questionsWithOptions: questionsList,
      authorization,
      comments,
      candidateQuestionnaire,
      diocese,
      diocese2,
      signedLogoBase64,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveCandidateScreeningForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Candidate Screening Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const saveTransferReleaseForm = async (
  order: Order,
  orderFormID: number,
  transferRelease: TransferRelease,
): Promise<boolean> => {
  try {
    const formData = {
      order,
      orderFormID,
      transferRelease,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveTransferReleaseForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Transfer Release Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const downloadMailingWindowOrder = async (
  order: Order,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadMailingWindowOrder`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(order),
      },
    );

    if (!response.ok) throw new Error("Failed to download Mailing Window");

    return response;
  } catch (error) {
    console.error("Error downloading Mailing Window:", error);
    return null;
  }
};

export const saveSocialMediaAuthorizationForm = async (
  order: Order,
  orderFormID: number,
  socialMediaRelease: SocialMediaRelease,
  legalNames: LegalName[],
  authorization: Authorization,
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  abbreviation: string,
  clientName: string,
  degree: Degree,
  employer: Employer,
  socialProfiles: SocialProfile[],
): Promise<boolean> => {
  try {
    const formData = {
      order,
      orderFormID,
      socialMediaRelease,
      legalNames,
      authorization,
      clientFirstName,
      clientLastName,
      clientTitle,
      diocese,
      abbreviation,
      clientName,
      degree,
      employer,
      socialProfiles,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/SaveSocialMediaAuthorizationForm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to save the Social Media Authorization Form.");
      return false;
    }

    console.log("Form successfully submitted and PDF generated.");
    return true;
  } catch (error) {
    console.error("Error while saving the form:", error);
    return false;
  }
};

export const downloadBlankBackgroundCheckAgreementForm = async (
  diocese: string,
): Promise<Response | null> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankBackgroundCheckAgreement`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ diocese }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Background Check Agreement Form");

    return response;
  } catch (error) {
    console.error("Error downloading Background Check Agreement Form:", error);
    return null;
  }
};

export const downloadBlankCriminalAuthForm = async (
  criminalYears: number,
  diocese: string,
  abbreviation: string,
  hasEmploymentService: boolean,
  hasEducation: boolean,
  oneEducation: boolean,
  hasMonitoringService: boolean,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankCriminalAuth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          criminalYears,
          diocese,
          abbreviation,
          hasEmploymentService,
          hasEducation,
          oneEducation,
          hasMonitoringService,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Criminal Authorization Form");

    return response;
  } catch (error) {
    console.error("Error downloading Criminal Authorization Form:", error);
    return null;
  }
};

export const downloadBlankCreditReportAuthForm = async (
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  abbreviation: string,
  clientName: string,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankCreditReportAuth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientFirstName,
          clientLastName,
          clientTitle,
          diocese,
          abbreviation,
          clientName,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Credit Report Authorization Form");

    return response;
  } catch (error) {
    console.error("Error downloading Credit Report Authorization Form:", error);
    return null;
  }
};

export const downloadBlankMotorVehicleAuthForm = async (
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  clientName: string,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankMotorVehicleAuth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientFirstName,
          clientLastName,
          clientTitle,
          diocese,
          clientName,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Motor Vehicle Authorization Form");

    return response;
  } catch (error) {
    console.error("Error downloading Motor Vehicle Authorization Form:", error);
    return null;
  }
};

export const downloadBlankReferenceAuthForm = async (
  clientID: number,
  clientDiocese: string,
  clientDiocese2: string,
  clientName: string,
  referenceOrder: ReferenceOrder,
  logoForm: AdditionalForm | null,
): Promise<Response | null> => {
  try {
    let signedLogoBase64 = "";
    if (logoForm) {
      const signedLogoUrl = await HttpService.getFileAdmin(
        "page-decorations",
        clientID.toString(),
        logoForm?.completedFormUrl,
      );

      const response = await fetch(signedLogoUrl);
      const blob = await response.blob();
      signedLogoBase64 = await convertBlobToBase64(blob);
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankReferenceAuth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientID,
          clientDiocese,
          clientDiocese2,
          clientName,
          referenceOrder,
          signedLogoBase64,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Reference Authorization Form");

    return response;
  } catch (error) {
    console.error("Error downloading Reference Authorization Form:", error);
    return null;
  }
};

export const downloadBlankCandidateQuestionnaire = async (
  clientID: number,
  orderFormID: number,
  candidateQuestionnaire: CandidateQuestionnaire,
  questionsWithOptions: Map<
    number,
    { questionText: string; options: QuestionOption[] }
  >,
  diocese: string,
  diocese2: string,
  logoForm: AdditionalForm | null,
): Promise<Response | null> => {
  let signedLogoBase64 = "";
  if (logoForm) {
    const signedLogoUrl = await HttpService.getFileAdmin(
      "page-decorations",
      clientID.toString(),
      logoForm?.completedFormUrl,
    );

    const response = await fetch(signedLogoUrl);
    const blob = await response.blob();
    signedLogoBase64 = await convertBlobToBase64(blob);
  }

  try {
    const questionsWithOptionsList = Array.from(
      questionsWithOptions,
      ([questionID, { questionText, options }]) => ({
        questionID,
        questionText,
        options,
      }),
    );

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankCandidateQuestionnaire`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderFormID,
          candidateQuestionnaire,
          questionsWithOptions: questionsWithOptionsList,
          diocese,
          diocese2,
          signedLogoBase64,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Candidate Questionnaire");

    return response;
  } catch (error) {
    console.error("Error downloading Candidate Questionnaire:", error);
    return null;
  }
};

export const downloadBlankIdentificationForm = async (
  referenceOrder: ReferenceOrder,
  diocese: string,
  clientID: number,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankIdentificationForm`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referenceOrder, diocese, clientID }),
      },
    );

    if (!response.ok) throw new Error("Failed to download Identification Form");

    return response;
  } catch (error) {
    console.error("Error downloading Identification Form:", error);
    return null;
  }
};

export const downloadBlankTransferReleaseForm =
  async (): Promise<Response | null> => {
    try {
      const response = await fetchWithAuth(
        `${apiBaseUrl}/api/OrderForm/DownloadBlankTransferRelease`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok)
        throw new Error("Failed to download Transfer Release Form");

      return response;
    } catch (error) {
      console.error("Error downloading Transfer Release Form:", error);
      return null;
    }
  };

export const downloadBlankSocialMediaAuthForm = async (
  clientFirstName: string,
  clientLastName: string,
  clientTitle: string,
  diocese: string,
  abbreviation: string,
  clientName: string,
): Promise<Response | null> => {
  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadBlankSocialMediaAuth`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientFirstName,
          clientLastName,
          clientTitle,
          diocese,
          abbreviation,
          clientName,
        }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Social Media Authorization Form");

    return response;
  } catch (error) {
    console.error("Error downloading Social Media Authorization Form:", error);
    return null;
  }
};

export const downloadPreFillTransferReleaseForm = async (
  order: Order,
  transferRelease: TransferRelease,
): Promise<Response | null> => {
  const formData = {
    order,
    transferRelease,
  };

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/OrderForm/DownloadPreFillTransferRelease`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok)
      throw new Error("Failed to download Transfer Release Form");

    return response;
  } catch (error) {
    console.error("Error downloading Transfer Release Form:", error);
    return null;
  }
};

export const determineOrderFormTypes = (
  client: Client | undefined,
  orderFormTypesResponse?: OrderFormType[] | null,
  referenceOrder?: ReferenceOrder | null,
  criminalOrder?: CriminalOrder | null,
  subscriptionOrder?: SubscriptionOrder | null,
) => {
  let availableFormTypes: OrderFormType[] = [];
  let isFiveYears = false;

  if (orderFormTypesResponse?.length) {
    const hasRef =
      referenceOrder !== undefined &&
      referenceOrder !== null &&
      referenceOrder.referenceOrderID !== 0;
    const hasCrim =
      criminalOrder !== undefined &&
      criminalOrder !== null &&
      criminalOrder.criminalOrderID !== 0;
    const hasSub =
      subscriptionOrder !== undefined &&
      subscriptionOrder !== null &&
      subscriptionOrder.subscriptionOrderID !== 0;

    if (hasRef) {
      isFiveYears =
        referenceOrder.years <= 5 && client?.denomination === "ACNA";
    }

    if (hasRef && hasCrim) {
      availableFormTypes = [...orderFormTypesResponse];
    } else if (hasRef) {
      availableFormTypes = orderFormTypesResponse.filter((f) =>
        [1, 5, 6, 7, 8].includes(f.orderFormTypeID),
      );
    } else if (hasCrim || hasSub) {
      availableFormTypes = orderFormTypesResponse.filter((f) =>
        [1, 2, 3, 4, 9].includes(f.orderFormTypeID),
      );
    }
  }

  availableFormTypes.push({
    orderFormTypeID: 0,
    description: "Mailing Window",
    formUrl: "",
    containerName: "",
  });

  const orderFormTypeOrder = [1, 2, 3, 4, 5, 6, 7, 9, 8];

  availableFormTypes.sort(
    (a, b) =>
      orderFormTypeOrder.indexOf(a.orderFormTypeID) -
      orderFormTypeOrder.indexOf(b.orderFormTypeID),
  );

  return {
    forms: availableFormTypes,
    isFiveYears,
  };
};
